import { Injectable } from '@angular/core';

import { ParsedCsv } from './csv-parsing.service';
import {
  ColumnMapping,
  ImportSchema,
  ImportSchemaField,
  SENTINEL_UNMAPPED,
} from '../interfaces/import-schema.interface';
import { BulkUploadProductPayloadDto } from '../interfaces/stock.interface';

export type RowConversionSeverity = 'error' | 'warning';

export interface RowConversionError {
  readonly rowNumber: number;
  readonly field: string;
  readonly rawValue: string;
  readonly reason: string;
  // 'error' = critical field failed, fila descartada.
  // 'warning' = non-critical field failed, valor reemplazado por null/default y la fila se importa igualmente.
  readonly severity: RowConversionSeverity;
}

export interface PayloadBuilderResult {
  readonly products: ReadonlyArray<BulkUploadProductPayloadDto>;
  readonly errors: ReadonlyArray<RowConversionError>;
}

@Injectable({ providedIn: 'root' })
export class PayloadBuilderService {
  build(
    parsed: ParsedCsv,
    schema: ImportSchema,
    mapping: ColumnMapping,
  ): PayloadBuilderResult {
    const headerIndex = this.buildHeaderIndex(parsed.headers);
    const products: BulkUploadProductPayloadDto[] = [];
    const errors: RowConversionError[] = [];

    for (let i = 0; i < parsed.rows.length; i++) {
      const rowNumber = i + 2;
      const row = parsed.rows[i];
      const criticalRowErrors: RowConversionError[] = [];
      const nonCriticalRowErrors: RowConversionError[] = [];
      const candidate: Partial<BulkUploadProductPayloadDto> = {};

      for (const field of schema.fields) {
        const csvHeader = mapping[field.property];
        if (!csvHeader || csvHeader === SENTINEL_UNMAPPED) {
          this.assignDefault(candidate, field);
          continue;
        }

        const columnIdx = headerIndex.get(csvHeader);
        if (columnIdx == null) {
          this.assignDefault(candidate, field);
          continue;
        }

        const rawValue = row[columnIdx] ?? '';
        const conversion = this.convertCell(rawValue, field);
        if (conversion.error) {
          const conversionError: RowConversionError = {
            rowNumber,
            field: field.property,
            rawValue,
            reason: conversion.error,
            severity: field.critical ? 'error' : 'warning',
          };
          if (field.critical) criticalRowErrors.push(conversionError);
          else nonCriticalRowErrors.push(conversionError);
          this.assignDefault(candidate, field);
          continue;
        }
        (candidate as Record<string, unknown>)[field.property] = conversion.value;
      }

      // Critical errors abort the row. Non-critical errors do not; the field gets a
      // null/default and the row is imported with a warning so the user can act on it later.
      if (criticalRowErrors.length > 0) {
        errors.push(...criticalRowErrors, ...nonCriticalRowErrors);
        continue;
      }

      const missingCritical = this.findMissingCritical(candidate, schema);
      if (missingCritical.length > 0) {
        for (const field of missingCritical) {
          errors.push({
            rowNumber,
            field: field.property,
            rawValue: '',
            reason: 'missing-critical-value',
            severity: 'error',
          });
        }
        errors.push(...nonCriticalRowErrors);
        continue;
      }

      errors.push(...nonCriticalRowErrors);
      products.push(candidate as BulkUploadProductPayloadDto);
    }

    return { products, errors };
  }

  private buildHeaderIndex(headers: ReadonlyArray<string>): Map<string, number> {
    const index = new Map<string, number>();
    for (let i = 0; i < headers.length; i++) {
      index.set(headers[i], i);
    }
    return index;
  }

  private convertCell(
    rawValue: string,
    field: ImportSchemaField,
  ): { value: unknown; error?: string } {
    const trimmed = rawValue.trim();

    if (trimmed.length === 0) {
      return field.critical && (field.type === 'int' || field.type === 'decimal' || field.type === 'string')
        ? { value: null, error: 'empty-critical' }
        : { value: this.nullDefault(field) };
    }

    switch (field.type) {
      case 'string':
        return { value: trimmed };
      case 'int': {
        const parsed = Number.parseInt(trimmed, 10);
        if (!Number.isFinite(parsed) || String(parsed) !== trimmed.replace(/^\+/, '')) {
          return { value: null, error: 'not-an-integer' };
        }
        return { value: parsed };
      }
      case 'decimal': {
        const separator = field.format?.decimalSeparator ?? '.';
        const normalized = separator === '.'
          ? trimmed.replace(/,/g, '')
          : trimmed.replace(/\./g, '').replace(separator, '.');
        const parsed = Number.parseFloat(normalized);
        if (!Number.isFinite(parsed)) return { value: null, error: 'not-a-decimal' };
        return { value: parsed };
      }
      case 'bool': {
        const normalizedBool = trimmed.toLowerCase();
        if (['true', 'yes', 'si', 'sí', '1'].includes(normalizedBool)) return { value: true };
        if (['false', 'no', '0'].includes(normalizedBool)) return { value: false };
        return { value: null, error: 'not-a-boolean' };
      }
      case 'date':
        return { value: trimmed };
      case 'enum': {
        if (!field.enumValues) return { value: trimmed };
        const exact = field.enumValues.find(v => v.value === trimmed);
        if (exact) return { value: exact.value };
        const lowered = trimmed.toLowerCase();
        const insensitive = field.enumValues.find(v => v.value.toLowerCase() === lowered);
        if (insensitive) return { value: insensitive.value };
        return { value: null, error: 'invalid-enum-value' };
      }
      default:
        return { value: trimmed };
    }
  }

  private assignDefault(
    candidate: Partial<BulkUploadProductPayloadDto>,
    field: ImportSchemaField,
  ): void {
    (candidate as Record<string, unknown>)[field.property] = this.nullDefault(field);
  }

  private nullDefault(field: ImportSchemaField): unknown {
    if (field.type === 'string') return '';
    return null;
  }

  private findMissingCritical(
    candidate: Partial<BulkUploadProductPayloadDto>,
    schema: ImportSchema,
  ): ReadonlyArray<ImportSchemaField> {
    const missing: ImportSchemaField[] = [];
    for (const field of schema.fields) {
      if (!field.critical) continue;
      const value = (candidate as Record<string, unknown>)[field.property];
      if (value === null || value === undefined || value === '') missing.push(field);
    }
    return missing;
  }
}
